/**
 * USER ID EXTRACTOR UTILITY (JavaScript version for Node.js API routes)
 * 
 * CRITICAL: This utility ensures that user IDs are ALWAYS converted to integers
 * before being passed to database operations. This prevents "invalid input syntax
 * for type integer" errors when user IDs are in formats like "db_453".
 * 
 * USE THIS EVERYWHERE you need to extract a user ID for database operations!
 */

import { getServiceRoleClient } from './serviceClient.js';

/**
 * Extract numeric user ID from any format
 * 
 * Handles:
 * - Direct numbers: 453
 * - String numbers: "453"
 * - db_ prefixed: "db_453"
 * - Any other string format that contains a number
 * 
 * @param {any} id - The ID value in any format
 * @returns {number|null} Numeric ID or null if extraction fails
 */
export function extractNumericId(id) {
 if (!id) return null;
 
 // If it's already a number, return it
 if (typeof id === 'number') {
 return Number.isInteger(id) && id > 0 ? id : null;
 }
 
 // Convert to string for processing
 const idStr = String(id);
 
 // Handle "db_XXX" format (including malformed "db_db_db_XXX")
 if (idStr.includes('db_')) {
 // Remove ALL occurrences of "db_" prefix (handles malformed IDs like "db_db_db_567")
 const numericPart = idStr.replace(/db_/g, '');
 const parsed = parseInt(numericPart, 10);
 return !isNaN(parsed) && parsed > 0 ? parsed : null;
 }
 
 // v4.0.57: Reject UUIDs and hex strings — parseInt('5e1dfdc0-...') silently returns 5
 if (idStr.includes('-') || idStr.length > 10 || !/^[0-9]+$/.test(idStr)) {
  return null;
 }

 // Try to parse as integer (only pure numeric strings reach here)
 const parsed = parseInt(idStr, 10);
 return !isNaN(parsed) && parsed > 0 ? parsed : null;
}

/**
 * Extract user ID from a user object with multiple fallback strategies
 * 
 * This function tries multiple paths to find the user ID:
 * 1. user.database_user.id
 * 2. user.user_metadata.user_id
 * 3. user.id (with format conversion)
 * 4. Database lookup by auth_user_id (for Supabase Auth UUIDs)
 * 
 * @param {any} user - The user object (can be from various auth sources)
 * @returns {Promise<number|null>} The numeric user ID or null
 */
export async function getUserIdFromObject(user) {
 if (!user) {
 console.error('[UserIdExtractor] No user object provided');
 return null;
 }

 console.log('[UserIdExtractor] Extracting user ID from object:', {
 hasUser: !!user,
 hasDatabaseUser: !!user.database_user,
 hasUserMetadata: !!user.user_metadata,
 userId: user.id,
 userIdType: typeof user.id
 });

 // Strategy 1: Check database_user.id
 if (user.database_user?.id) {
 const numericId = extractNumericId(user.database_user.id);
 if (numericId) {
 console.log('[UserIdExtractor] Extracted from database_user.id:', numericId);
 return numericId;
 }
 }

 // Strategy 2: Check user_metadata.user_id
 if (user.user_metadata?.user_id) {
 const numericId = extractNumericId(user.user_metadata.user_id);
 if (numericId) {
 console.log('[UserIdExtractor] Extracted from user_metadata.user_id:', numericId);
 return numericId;
 }
 }

 // Strategy 3: Try direct user.id extraction
 if (user.id) {
 const numericId = extractNumericId(user.id);
 if (numericId) {
 console.log('[UserIdExtractor] Extracted from user.id:', numericId);
 return numericId;
 }

 // Strategy 4: If user.id looks like a UUID, try database lookup
 const idStr = String(user.id);
 if (idStr.includes('-') || idStr.length > 20) {
 console.log('[UserIdExtractor] user.id appears to be UUID, attempting database lookup');
 try {
 const supabase = getServiceRoleClient();
 const { data, error } = await supabase
 .from('users')
 .select('id')
 .eq('auth_user_id', user.id)
 .single();

 if (error) {
 console.error('[UserIdExtractor] Database lookup failed:', error.message);
 return null;
 }

 if (data?.id) {
 const numericId = extractNumericId(data.id);
 console.log('[UserIdExtractor] Extracted from database lookup:', numericId);
 return numericId;
 }
 } catch (error) {
 console.error('[UserIdExtractor] Exception during database lookup:', error);
 return null;
 }
 }
 }

 console.error('[UserIdExtractor] Failed to extract user ID from any source');
 return null;
}

/**
 * Validate that a value is a valid integer user ID
 * 
 * @param {any} userId - The value to validate
 * @returns {boolean} True if valid integer user ID
 */
export function isValidUserId(userId) {
 return typeof userId === 'number' && Number.isInteger(userId) && userId > 0;
}

/**
 * Assert that a user ID is valid, throw error if not
 * 
 * @param {any} userId - The user ID to validate
 * @param {string} context - Context string for error message (e.g., "payment creation")
 * @throws {Error} if user ID is invalid
 */
export function assertValidUserId(userId, context = 'operation') {
 if (!isValidUserId(userId)) {
 throw new Error(
 `Invalid user ID for ${context}: ${JSON.stringify(userId)}. ` +
 `Expected a positive integer, got ${typeof userId}.`
 );
 }
}

